/**
 * Orders 相關的 SWR Hooks
 */

import { useOrdersGetByUser, type OrdersGetByUserParams } from '@pos/api-sdk';
import { getApiHeaders, getTenantIdFromUrl } from '../../api/apiUtils';
import { useTenant } from '../../contexts/TenantContext';
import { swrListConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';

/**
 * 取得使用者的訂單列表（userId 為會員/客戶 UUID）
 */
export function useUserOrders(
  userId: string | undefined,
  params?: OrdersGetByUserParams,
  options?: {
    fetch?: RequestInit;
    revalidateOnFocus?: boolean;
    dedupingInterval?: number;
    enabled?: boolean;
    swr?: SWRConfiguration;
  }
) {
  // 優先使用 TenantContext 的 tenantId，如果沒有則從 URL 取得
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = false,
    dedupingInterval = 1000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  // 取得 headers 並確保正確傳遞
  const apiHeaders = getApiHeaders(tenantId);

  const result = useOrdersGetByUser(userId ?? '', params, {
    fetch: {
      ...options?.fetch,
      headers: {
        ...apiHeaders,
        ...(options?.fetch?.headers || {}),
      },
    },
    swr: {
      ...swrListConfig,
      enabled: enabled && !!userId && userId.length > 0 && !!tenantId,
      revalidateOnFocus,
      revalidateOnReconnect: false,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料與分頁
  const ordersData = result.data?.data;
  const orders =
    ordersData && 'data' in ordersData && Array.isArray(ordersData.data)
      ? ordersData.data
      : undefined;
  const pagination =
    ordersData && 'pagination' in ordersData && ordersData.pagination
      ? ordersData.pagination
      : undefined;

  return {
    orders,
    pagination,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
